import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Star, Copy, Mic, CheckCircle } from 'lucide-react';
import { voiceSettingsService, VoiceSettings, CreateVoiceSettingsRequest } from '../../services/voiceSettingsService';
import VoiceSettingsEditor from './VoiceSettingsEditor';
import toast from 'react-hot-toast';

interface VoiceSettingsManagerProps {
  onSelectVoiceSetting?: (voiceSetting: VoiceSettings) => void;
  selectedVoiceSettingId?: number | null;
  compact?: boolean; // For use in modals/smaller spaces
}

const VoiceSettingsManager: React.FC<VoiceSettingsManagerProps> = ({
  onSelectVoiceSetting,
  selectedVoiceSettingId,
  compact = false
}) => {
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingVoiceSetting, setEditingVoiceSetting] = useState<VoiceSettings | null>(null);
  const [filter, setFilter] = useState<'all' | 'default' | 'custom'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchVoiceSettings();
  }, []);

  const fetchVoiceSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 VoiceSettingsManager: Fetching voice settings...');

      const data = await voiceSettingsService.getVoiceSettings();
      console.log('🎤 VoiceSettingsManager: Received voice settings:', data);

      setVoiceSettings(data);
    } catch (err) {
      console.error('❌ VoiceSettingsManager: Error fetching voice settings:', err);
      setError('Failed to fetch voice settings');
      toast.error('Failed to load voice settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVoiceSetting = () => {
    setEditingVoiceSetting(null);
    setShowEditor(true);
  };

  const handleEditVoiceSetting = (voiceSetting: VoiceSettings) => {
    setEditingVoiceSetting(voiceSetting);
    setShowEditor(true);
  };

  const handleDeleteVoiceSetting = async (voiceSetting: VoiceSettings) => {
    if (!window.confirm(`Are you sure you want to delete "${voiceSetting.name}"?`)) {
      return;
    }

    try {
      await voiceSettingsService.deleteVoiceSetting(voiceSetting.id);
      toast.success('Voice setting deleted successfully');
      fetchVoiceSettings();
    } catch (err) {
      console.error('❌ Error deleting voice setting:', err);
      toast.error('Failed to delete voice setting');
    }
  };

  const handleSetDefault = async (voiceSetting: VoiceSettings) => {
    try {
      await voiceSettingsService.setDefaultVoiceSetting(voiceSetting.id);
      toast.success(`"${voiceSetting.name}" set as default`);
      fetchVoiceSettings();
    } catch (err) {
      console.error('❌ Error setting default voice setting:', err);
      toast.error('Failed to set default voice setting');
    }
  };

  const handleCloneVoiceSetting = async (voiceSetting: VoiceSettings) => {
    const cloneData: CreateVoiceSettingsRequest = {
      name: `${voiceSetting.name} (Copy)`,
      description: voiceSetting.description,
      tone: voiceSetting.tone,
      perspective: voiceSetting.perspective,
      style: voiceSetting.style,
      avoid_terms: [...voiceSetting.avoid_terms],
      include_elements: [...voiceSetting.include_elements],
      preferred_length: voiceSetting.preferred_length,
      cta_style: voiceSetting.cta_style,
      is_default: false
    };

    try {
      await voiceSettingsService.createVoiceSetting(cloneData);
      toast.success('Voice setting cloned successfully');
      fetchVoiceSettings();
    } catch (err) {
      console.error('❌ Error cloning voice setting:', err);
      toast.error('Failed to clone voice setting');
    }
  };

  const handleEditorSave = async (data: CreateVoiceSettingsRequest) => {
    try {
      if (editingVoiceSetting) {
        await voiceSettingsService.updateVoiceSetting(editingVoiceSetting.id, data);
        toast.success('Voice setting updated successfully');
      } else {
        await voiceSettingsService.createVoiceSetting(data);
        toast.success('Voice setting created successfully');
      }

      setShowEditor(false);
      setEditingVoiceSetting(null);
      fetchVoiceSettings();
    } catch (err) {
      console.error('❌ Error saving voice setting:', err);
      toast.error('Failed to save voice setting');
    }
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingVoiceSetting(null);
  };

  const filteredVoiceSettings = voiceSettings
    .filter(setting => {
      if (filter === 'default') return setting.is_default;
      if (filter === 'custom') return !setting.is_default;
      return true;
    })
    .filter(setting =>
      setting.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      setting.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      setting.tone.toLowerCase().includes(searchTerm.toLowerCase())
    );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-center p-4">
        <p>{error}</p>
        <button
          onClick={fetchVoiceSettings}
          className="mt-2 px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-4' : 'space-y-6'}>
      {!compact && (
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <Mic className="h-6 w-6 mr-2" />
              Voice Settings
            </h2>
            <p className="text-gray-600">
              Manage the tone and style of your AI-generated campaign content
            </p>
          </div>
          <button
            onClick={handleCreateVoiceSetting}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Voice Setting
          </button>
        </div>
      )}

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({voiceSettings.length})
          </button>
          <button
            onClick={() => setFilter('default')}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === 'default'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Default ({voiceSettings.filter(s => s.is_default).length})
          </button>
          <button
            onClick={() => setFilter('custom')}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === 'custom'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Custom ({voiceSettings.filter(s => !s.is_default).length})
          </button>
        </div>

        <div className="flex-1">
          <input
            type="text"
            placeholder="Search voice settings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {compact && (
          <button
            onClick={handleCreateVoiceSetting}
            className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-1" />
            New
          </button>
        )}
      </div>

      {/* Voice Settings Grid */}
      <div className={`grid gap-4 ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
        {filteredVoiceSettings.map((voiceSetting) => (
          <div
            key={voiceSetting.id}
            className={`bg-white rounded-lg border-2 transition-all cursor-pointer hover:shadow-md ${
              selectedVoiceSettingId === voiceSetting.id
                ? 'border-blue-500 bg-blue-50'
                : voiceSetting.is_default
                ? 'border-yellow-300 bg-yellow-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => onSelectVoiceSetting?.(voiceSetting)}
          >
            <div className="p-4">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <h3 className="font-semibold text-gray-900">{voiceSetting.name}</h3>
                  {voiceSetting.is_default && (
                    <Star className="h-4 w-4 text-yellow-500 fill-current" />
                  )}
                  {selectedVoiceSettingId === voiceSetting.id && (
                    <CheckCircle className="h-4 w-4 text-blue-500 fill-current" />
                  )}
                </div>

                <div className="flex items-center space-x-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditVoiceSetting(voiceSetting);
                    }}
                    className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloneVoiceSetting(voiceSetting);
                    }}
                    className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                    title="Clone"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  {!voiceSetting.is_default && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetDefault(voiceSetting);
                      }}
                      className="p-1 text-gray-500 hover:text-yellow-600 transition-colors"
                      title="Set as Default"
                    >
                      <Star className="h-4 w-4" />
                    </button>
                  )}
                  {!voiceSetting.is_default && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteVoiceSetting(voiceSetting);
                      }}
                      className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Description */}
              {voiceSetting.description && (
                <p className="text-sm text-gray-600 mb-3">{voiceSetting.description}</p>
              )}

              {/* Voice Details */}
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Tone:</span>
                  <span className="ml-2 text-gray-600">{voiceSetting.tone}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Style:</span>
                  <span className="ml-2 text-gray-600">{voiceSetting.style}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Length:</span>
                  <span className="ml-2 text-gray-600">{voiceSetting.preferred_length}</span>
                </div>
              </div>

              {/* Elements Preview */}
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex flex-wrap gap-1">
                  {voiceSetting.include_elements.slice(0, 3).map((element, index) => (
                    <span
                      key={index}
                      className="inline-block px-2 py-1 text-xs bg-green-100 text-green-700 rounded"
                    >
                      {element}
                    </span>
                  ))}
                  {voiceSetting.include_elements.length > 3 && (
                    <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                      +{voiceSetting.include_elements.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredVoiceSettings.length === 0 && (
        <div className="text-center py-12">
          <Mic className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No voice settings found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm
              ? 'Try adjusting your search terms'
              : 'Create your first voice setting to get started'
            }
          </p>
          {!searchTerm && (
            <button
              onClick={handleCreateVoiceSetting}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Voice Setting
            </button>
          )}
        </div>
      )}

      {/* Voice Settings Editor Modal */}
      {showEditor && (
        <VoiceSettingsEditor
          voiceSetting={editingVoiceSetting}
          onSave={handleEditorSave}
          onClose={handleEditorClose}
        />
      )}
    </div>
  );
};

export default VoiceSettingsManager;